import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  Spinner,
  Subtitle2,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataContext } from "../../context/DataContext";
import { ApiContext } from "../../Root";
import { useAuth } from "../../authentication/AuthContextProvider";
import { ITicket } from "../../interfaces/ITicket";
import { TicketBadge } from "../common/TicketBadge";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalM,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rowLeft: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  rowTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  rowSub: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  rowDate: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
  },
  loader: {
    marginTop: tokens.spacingVerticalL,
  },
  divider: {
    alignSelf: "center",
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorBrandBackground,
  },
  empty: {
    marginTop: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
  },
  // detail dialog
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: tokens.spacingVerticalL,
  },
  dialogMeta: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  dialogTicketId: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  dialogSubject: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    marginTop: "2px",
  },
  dialogCreated: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
  },
  sectionTitle: {
    color: tokens.colorNeutralForeground3,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  fieldValue: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
  },
  faultBox: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase300,
    whiteSpace: "pre-wrap",
    lineHeight: tokens.lineHeightBase400,
    minHeight: "60px",
  },
  lastUpdated: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: tokens.spacingVerticalL,
  },
  lastUpdatedLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
  lastUpdatedValue: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  commentBox: {
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase300,
    whiteSpace: "pre-wrap",
    lineHeight: tokens.lineHeightBase400,
    marginBottom: tokens.spacingVerticalL,
  },
  sectionDivider: {
    marginBottom: tokens.spacingVerticalL,
  },
});

export const Tickets = (): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  const dataContext = useContext(DataContext);
  const apiContext = useContext(ApiContext);
  const auth = useAuth();
  const { tickets, isLoadingTickets, isLoadingMoreTickets, ticketsNextPageToken, selectedCustomer } = dataContext.state;

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<ITicket | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && ticketsNextPageToken && !isLoadingMoreTickets && !isLoadingTickets) {
          if (!selectedCustomer || !auth.user) return;
          apiContext.customerOrderApi.getTickets(dataContext, auth.user, selectedCustomer, t, ticketsNextPageToken);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.unobserve(el);
  }, [ticketsNextPageToken, isLoadingMoreTickets, isLoadingTickets]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Divider appearance="brand" className={styles.divider} />
      {isLoadingTickets ? (
        <Spinner className={styles.loader} size="large" />
      ) : tickets.length === 0 ? (
        <p className={styles.empty}>{t("labels.noTickets")}</p>
      ) : (
        <div className={styles.list}>
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              className={styles.row}
              onClick={() => setSelected(ticket)}
            >
              <div className={styles.rowLeft}>
                <span className={styles.rowTitle}>
                  #{ticket.ticketNumber} {ticket.faultSymptom.split("\n")[0].slice(0, 50)}
                </span>
                <span className={styles.rowSub}>
                  {t("labels.orderNumber")}: {ticket.orderNumber}
                </span>
                <span className={styles.rowSub}>
                  {t("labels.category")}: {ticket.category}
                </span>
                <span className={styles.rowSub}>
                  {t("labels.componentName")}: {ticket.componentName}
                </span>
              </div>

              <span className={styles.rowDate}>
                <TicketBadge ticket={ticket} />
                {new Date(ticket.created).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}

      <div ref={sentinelRef} />
      {isLoadingMoreTickets && (
        <Spinner size="medium" label={t("loadingMoreTickets")} style={{ marginTop: "12px", marginBottom: "12px" }} />
      )}

      {selected && (
        <Dialog open onOpenChange={() => setSelected(null)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                <div className={styles.dialogHeader}>
                  <div className={styles.dialogMeta}>
                    <span className={styles.dialogTicketId}>#{selected.ticketNumber}</span>
                    <span className={styles.dialogSubject}>{selected.faultSymptom.split("\n")[0].slice(0, 80)}</span>
                    <Caption1 className={styles.dialogCreated}>
                      {new Date(selected.created).toLocaleDateString("sv-SE", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </Caption1>
                  </div>
                  <TicketBadge ticket={selected} />
                </div>
              </DialogTitle>
              <DialogContent>
                {selected.comment && (
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>{t("labels.comment")}</span>
                    <div className={styles.commentBox}>{selected.comment}</div>
                  </div>
                )}

                <Divider className={styles.sectionDivider} />

                <div className={styles.section}>
                  <Subtitle2 className={styles.sectionTitle}>
                    {t("labels.contact")}
                  </Subtitle2>
                  <div className={styles.twoCol}>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("labels.reportedBy")}
                      </span>
                      <span className={styles.fieldValue}>
                        {selected.reportedBy}
                      </span>
                    </div>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("labels.phoneNumber")}
                      </span>
                      <span className={styles.fieldValue}>
                        {selected.phoneNumber}
                      </span>
                    </div>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("labels.email")}
                      </span>
                      <span className={styles.fieldValue}>
                        {selected.email}
                      </span>
                    </div>
                  </div>
                </div>

                <Divider className={styles.sectionDivider} />

                <div className={styles.section}>
                  <Subtitle2 className={styles.sectionTitle}>
                    {t("labels.ticketDetails")}
                  </Subtitle2>
                  <div className={mergeClasses(styles.twoCol)}>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("labels.ticketNumber")}
                      </span>
                      <span className={styles.fieldValue}>
                        {selected.ticketNumber}
                      </span>
                    </div>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("labels.orderNumber")}
                      </span>
                      <span className={styles.fieldValue}>
                        {selected.orderNumber}
                      </span>
                    </div>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("labels.category")}
                      </span>
                      <span className={styles.fieldValue}>
                        {selected.category}
                      </span>
                    </div>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("labels.componentName")}
                      </span>
                      <span className={styles.fieldValue}>
                        {selected.componentName}
                      </span>
                    </div>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("labels.replacementMaterialWanted")}
                      </span>
                      <span className={styles.fieldValue}>
                        {selected.replacementMaterial ? t("labels.yes") : t("labels.no")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {t("labels.faultSymptom")}
                  </span>
                  <div className={styles.faultBox}>{selected.faultSymptom}</div>
                </div>

                <div className={styles.lastUpdated}>
                  <span className={styles.lastUpdatedLabel}>{t("labels.lastUpdated")}</span>
                  <span className={styles.lastUpdatedValue}>
                    {new Date(selected.modified).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" })}
                  </span>
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="secondary"
                  onClick={() => setSelected(null)}
                >
                  {t("buttons.close")}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );
};
